import React, { memo } from "react";
import { useTable, Column } from "react-table";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";

const MAX_PROCEDURE_MATRIX_COLUMNS = 6;

const capitalizeFirst = (s: string): string =>
  s && s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;

type ProcedureCodeCustomAnswer = {
  procedure_code: string;
  custom_answers: Record<string, string>;
};

type ProcedureCodeMatrix = {
  rows: Record<string, string>[];
  baseHeaders: string[];
};

type ProcedureCodeGroup = {
  procedureCode: string;
  entries: Array<{
    questionId: string;
    questionText: string;
    answer: string;
  }>;
};

const stripProcedureCode = (text: string, code: string): string => {
  if (!text || !code) return text;

  // Build code variants for format mismatch
  const variants = new Set<string>([code]);
  const dMatch = code.match(/^([dD])(\d+)$/);
  if (dMatch) {
    variants.add(dMatch[2]); // d3410 -> also try 3410
  } else if (/^\d+$/.test(code)) {
    variants.add("d" + code).add("D" + code); // 3410 -> also try d3410, D3410
  }

  let result = text;
  for (const variant of variants) {
    const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Remove (Code: variant) prefix
    result = result.replace(new RegExp(`^\\s*\\(Code:\\s*${escaped}\\)\\s*`, "gi"), "");
    // Remove variant from anywhere in text (word boundary)
    result = result.replace(new RegExp(`\\b${escaped}\\b`, "gi"), "");
  }

  // Clean up: trailing ?, extra spaces, orphaned punctuation (e.g. "For , what" -> "For what")
  result = result.replace(/\?+\s*$/, "").trim();
  result = result.replace(/\s+/g, " ").trim();
  result = result
    .replace(/\s*,\s*,\s*/g, ", ")
    .replace(/\s,\s/g, ", ")
    .replace(/^\s*,\s*/, "")
    .replace(/\s*,\s*$/, "")
    .trim();
  return result || text;
};

const buildProcedureCodeMatrix = (
  procedureCodeCustomAnswers: ProcedureCodeCustomAnswer[],
  questionTextMap: Record<string, string>
): ProcedureCodeMatrix | null => {
  if (procedureCodeCustomAnswers.length === 0) return null;

  const baseToColumn = new Map<string, string>();
  const baseOrder: string[] = [];

  procedureCodeCustomAnswers.forEach((item) => {
    Object.entries(item.custom_answers).forEach(([questionId]) => {
      const fullText = questionTextMap[questionId];
      if (fullText) {
        const base = stripProcedureCode(fullText, item.procedure_code);
        if (base && !baseToColumn.has(base)) {
          baseToColumn.set(base, base);
          baseOrder.push(base);
        }
      }
    });
  });

  if (baseOrder.length === 0) {
    return null;
  }

  const rows = procedureCodeCustomAnswers.map((item) => {
    const row: Record<string, string> = { procedureCode: item.procedure_code };
    baseOrder.forEach((baseHeader) => {
      const matchingEntry = Object.entries(item.custom_answers).find(
        ([qId]) => stripProcedureCode(questionTextMap[qId] || "", item.procedure_code) === baseHeader
      );
      const answer = matchingEntry?.[1];
      row[baseHeader] = answer != null && answer !== "" ? answer : "-";
    });
    return row;
  });

  return { rows, baseHeaders: baseOrder };
};

const buildPerProcedureGroups = (
  procedureCodeCustomAnswers: ProcedureCodeCustomAnswer[],
  questionTextMap: Record<string, string>
): ProcedureCodeGroup[] =>
  procedureCodeCustomAnswers
    .map((item) => ({
      procedureCode: item.procedure_code,
      entries: Object.entries(item.custom_answers)
        .filter(([questionId]) => questionTextMap[questionId])
        .map(([questionId, answer]) => ({
          questionId,
          questionText: questionTextMap[questionId],
          answer,
        })),
    }))
    .filter((group) => group.entries.length > 0);

interface CustomAnswersTableProps {
  title: string;
  customAnswers: Record<string, string>;
  questionTextMap: Record<string, string>;
  includeProcedureCode?: boolean;
  procedureCode?: string;
  // For procedure codes: array of { procedure_code, custom_answers }
  procedureCodeCustomAnswers?: Array<{
    procedure_code: string;
    custom_answers: Record<string, string>;
  }>;
}

const CustomAnswersTableComponent: React.FC<CustomAnswersTableProps> = ({
  title,
  customAnswers,
  questionTextMap,
  includeProcedureCode = false,
  procedureCode,
  procedureCodeCustomAnswers,
}) => {
  // Matrix layout: rows = procedure codes, columns = base question types (e.g. Fees, Coverage percentage)
  const procedureCodeMatrix = React.useMemo(() => {
    if (!procedureCodeCustomAnswers || procedureCodeCustomAnswers.length === 0) return null;
    return buildProcedureCodeMatrix(procedureCodeCustomAnswers, questionTextMap);
  }, [procedureCodeCustomAnswers, questionTextMap]);

  const procedureRenderMode = React.useMemo(() => {
    if (!procedureCodeMatrix) return null;
    return procedureCodeMatrix.baseHeaders.length > MAX_PROCEDURE_MATRIX_COLUMNS
      ? "perProcedure"
      : "matrix";
  }, [procedureCodeMatrix]);

  const perProcedureGroups = React.useMemo(() => {
    if (!procedureCodeCustomAnswers || procedureRenderMode !== "perProcedure") {
      return [];
    }

    return buildPerProcedureGroups(procedureCodeCustomAnswers, questionTextMap);
  }, [procedureCodeCustomAnswers, procedureRenderMode, questionTextMap]);

  // Flat layout for category-level custom answers (plan info, maximums, etc.)
  const flatData = React.useMemo(() => {
    if (procedureCodeCustomAnswers && procedureCodeCustomAnswers.length > 0) return [];

    return Object.entries(customAnswers)
      .filter(([questionId]) => questionTextMap[questionId])
      .map(([questionId, answer]) => ({
        questionId,
        questionText: questionTextMap[questionId],
        answer,
        ...(includeProcedureCode && procedureCode ? { procedureCode } : {}),
      }));
  }, [customAnswers, questionTextMap, includeProcedureCode, procedureCode, procedureCodeCustomAnswers]);

  const data = procedureRenderMode === "matrix" && procedureCodeMatrix
    ? procedureCodeMatrix.rows
    : flatData;

  const columns: Column<any>[] = React.useMemo(() => {
    const cols: Column<any>[] = [];

    if (procedureRenderMode === "matrix" && procedureCodeMatrix) {
      cols.push({ Header: "Procedure Code", accessor: "procedureCode" });
      procedureCodeMatrix.baseHeaders.forEach((baseHeader) => {
        const headerLower = baseHeader.toLowerCase();
        const isPercentage = headerLower.includes("percentage");
        const isFee = headerLower.includes("fee");
        cols.push({
          Header: capitalizeFirst(baseHeader),
          accessor: baseHeader,
          Cell: ({ value }: { value: string }) => {
            if (value == null || value === "" || value === "-") return "-";
            // Only format if value is a simple number; leave descriptive text (e.g. "20% discount on office fee") as-is
            const isSimpleNumber = /^(\$)?[\d,]+(\.\d+)?(%)?$/.test(value.trim());
            if (!isSimpleNumber) return value;
            if (isPercentage) return value.endsWith("%") ? value : `${value}%`;
            if (isFee) return value.startsWith("$") ? value : `$${value}`;
            return value;
          },
        });
      });
      return cols;
    }

    const hasProcedureCodes =
      (includeProcedureCode && procedureCode) ||
      (procedureCodeCustomAnswers && procedureCodeCustomAnswers.length > 0);

    if (hasProcedureCodes) {
      cols.push({ Header: "Procedure Code", accessor: "procedureCode" });
    }
    cols.push(
      {
        Header: "Question",
        accessor: "questionText",
        Cell: ({ value }: { value: string }) => (value ? capitalizeFirst(value) : value),
      },
      { Header: "Answer", accessor: "answer" }
    );
    return cols;
  }, [
    procedureRenderMode,
    procedureCodeMatrix,
    includeProcedureCode,
    procedureCode,
    procedureCodeCustomAnswers,
  ]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data,
    });

  if (procedureRenderMode === "perProcedure" && perProcedureGroups.length === 0) {
    return null;
  }

  if (procedureRenderMode !== "perProcedure" && data.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6 mt-4">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-gray-900">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {procedureRenderMode === "perProcedure" ? (
          <div className="divide-y divide-gray-100">
            {perProcedureGroups.map((group) => (
              <div key={group.procedureCode} className="px-6 py-5">
                <div className="mb-3 text-sm font-semibold text-gray-900">
                  Procedure Code: {group.procedureCode}
                </div>
                <div className="overflow-x-auto select-text" style={{ contain: "layout" }}>
                  <table className="w-full table-fixed select-text">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Question
                        </th>
                        <th className="w-48 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Answer
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {group.entries.map((entry) => (
                        <tr
                          key={entry.questionId}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-gray-900 whitespace-normal break-words align-top select-text">
                            {capitalizeFirst(entry.questionText)}
                          </td>
                          <td className="w-48 px-6 py-4 text-sm text-gray-900 whitespace-normal break-words align-top select-text">
                            {entry.answer != null && entry.answer !== "" ? entry.answer : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto select-text" style={{ contain: "layout" }}>
            <table {...getTableProps()} className="w-full select-text">
              <thead className="bg-gray-50 border-b border-gray-200">
                {headerGroups.map((headerGroup, groupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={groupIndex}>
                    {headerGroup.headers.map((column, headerIndex) => (
                      <th
                        {...column.getHeaderProps()}
                        key={headerIndex}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                {...getTableBodyProps()}
                className="bg-white divide-y divide-gray-100"
              >
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          key={cellIndex}
                          className="px-6 py-4 text-sm text-gray-900 select-text"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CustomAnswersTable = memo(CustomAnswersTableComponent);
export default CustomAnswersTable;
